"use client";

import { useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  CENTER, drawFrame, FACE_RADIUS, FEATURE_SCALE, GROUND, HAT, HAT_POSES, HAT_STYLES, IDLE_GLANCES, INK, moodBeat, POSES, restingTassel, RIVA_LABELS,
  SPARKLE_PATH, SPARKLES, tasselShape, TUFT_PATH, TUFT_STRANDS, VIEW,
  type Frame, type HatState, type HatStyle, type Pose, type RivaGesture, type RivaMood,
} from "./riva-rig";

export { HAT_STYLES, RIVA_LABELS, RIVA_MOODS, type HatStyle, type RivaGesture, type RivaMood } from "./riva-rig";

export type RivaProps = {
  mood?: RivaMood;
  /** Pixel size of the square frame; without it Riva fills the width of its container. */
  size?: number;
  followPointer?: boolean;
  /** Pause all movement, for example in a hidden panel. */
  active?: boolean;
  /** Increment `id` for every request, including repeats. */
  gesture?: { type: RivaGesture; id: number };
  /** Opt in only where Riva stands alone, never inside a link or button. */
  interactive?: boolean;
  shadow?: boolean;
  /** Het afstudeerhoedje. Zonder hoedje blijft alleen het gezicht over, bijgesneden tot de cirkel. */
  hat?: HatStyle | false;
  decorative?: boolean;
  className?: string;
};

type Spring = { value: number; velocity: number };
const spring = (value = 0): Spring => ({ value, velocity: 0 });
/** Substepped semi-implicit Euler: stable at any frame rate. */
function advance(s: Spring, target: number, dt: number, k: number, d: number) {
  const steps = Math.max(1, Math.ceil(dt * 240));
  const h = dt / steps;
  for (let i = 0; i < steps; i++) {
    s.velocity += (k * (target - s.value) - d * s.velocity) * h;
    s.value += s.velocity * h;
  }
  return s.value;
}

// Eyes and gaze react quickly; the body is heavier.
const STIFFNESS: Partial<Record<keyof Pose, [number, number]>> = {
  lookX: [260, 26], lookY: [260, 26], openL: [360, 32], openR: [360, 32], happy: [240, 26], sag: [200, 26],
  browY: [260, 24], browTilt: [240, 24], browSkew: [240, 24], smile: [220, 24], mouthOpen: [300, 26],
  tilt: [110, 18], lift: [140, 19], turn: [100, 19], hold: [100, 16], headTilt: [110, 13],
};
const GESTURE_LENGTH: Record<RivaGesture, number> = { hop: 0.75, wave: 1.5, nod: 0.9, wiggle: 0.9 };

const clamp = (v: number, limit: number) => Math.max(-limit, Math.min(limit, v));
type Point = [number, number];
/** Zwaartekracht voor hoedje en kwastje, in gezichtseenheden per s². */
const GRAVITY = 1250;
/** Het kwastje is een slinger: een licht koord met een zware pluim. Eén zwaai duurt ongeveer een seconde. */
const TASSEL_GRAVITY = 950;
/** Wat er per stap van 1/240 s aan snelheid overblijft: na een seconde is de zwaai grotendeels uitgedempt. */
const TASSEL_DAMPING = 0.993;
/** Verder dan dit (graden van verticaal) zwaait de pluim niet uit. */
const TASSEL_REACH = 70;
/** Hoe hard het hoedje omhoog gaat bij het begin van een stemming (eenheden/s). */
const HAT_KICK: Partial<Record<RivaMood, number>> = { surprised: 230, idea: 170, proud: 90, happy: 70, celebrate: 330 };

function restFrame(mood: RivaMood) {
  const pose = POSES[mood];
  return drawFrame({ pose, x: 0, squash: 0, blink: 0, t: 0, hat: { ...HAT_POSES[mood], h: 0, yaw: HAT.yaw + pose.turn * 20 } });
}

/** Riva: the eyes lead, the soft body follows, the hat and its tassel trail behind. */
export function Riva({ mood = "idle", size, followPointer = true, active = true, gesture, interactive = false, shadow = true, hat = "kleur", decorative = false, className }: RivaProps) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const root = useRef<HTMLSpanElement>(null);
  const controls = useRef({ mood, followPointer, gesture, active, hat });
  useEffect(() => { controls.current = { mood, followPointer, gesture, active, hat }; }, [mood, followPointer, gesture, active, hat]);
  // The animation loop owns the drawing after the first paint. React keeps this
  // frame, so a mood change never flashes the end pose for a single frame.
  const [initial] = useState(() => restFrame(mood));
  const tap = useRef<() => void>(() => {});
  const wakeRef = useRef<() => void>(() => {});
  // Het hoedje voegt onderdelen toe; de lus zoekt ze bij de start op.
  const hasHat = Boolean(hat);

  useEffect(() => {
    const node = root.current;
    if (!node) return;
    const q = <T extends Element>(part: string) => node.querySelector<T>(`[data-part="${part}"]`)!;
    const el = {
      body: q<SVGGElement>("body"), head: q<SVGGElement>("head"), shadow: q<SVGEllipseElement>("shadow"), highlight: q<SVGRadialGradientElement>("highlight"),
      eyes: [q<SVGGElement>("eye-l"), q<SVGGElement>("eye-r")], eyeShapes: [q<SVGPathElement>("eye-shape-l"), q<SVGPathElement>("eye-shape-r")],
      shines: [q<SVGGElement>("shine-l"), q<SVGGElement>("shine-r")], brows: [q<SVGPathElement>("brow-l"), q<SVGPathElement>("brow-r")],
      mouth: q<SVGGElement>("mouth"), mouthShape: q<SVGPathElement>("mouth-shape"), mouthClip: q<SVGPathElement>("mouth-clip"), tongue: q<SVGEllipseElement>("tongue"),
      cheeks: q<SVGGElement>("cheeks"), cheekL: q<SVGEllipseElement>("cheek-l"), cheekR: q<SVGEllipseElement>("cheek-r"),
      sparkles: SPARKLES.map((_, i) => q<SVGPathElement>(`sparkle-${i}`)), zzz: [q<SVGTextElement>("z-0"), q<SVGTextElement>("z-1")],
      hat: q<SVGGElement>("hat"), hatSkull: q<SVGPathElement>("hat-skull"), hatEdge: q<SVGPathElement>("hat-edge"), hatBoard: q<SVGPathElement>("hat-board"),
      hatCord: q<SVGPathElement>("hat-cord"), tassel: q<SVGPathElement>("tassel"), tuft: q<SVGGElement>("tuft"),
    };
    const set = (target: Element, name: string, value: string | number) => {
      const next = String(value);
      if (target.getAttribute(name) !== next) target.setAttribute(name, next);
    };
    const paint = (frame: Frame, tassel: { end: Point; bend: number }) => {
      set(el.body, "transform", frame.body);
      set(el.shadow, "rx", frame.shadow.rx);
      set(el.shadow, "opacity", frame.shadow.opacity);
      set(el.head, "transform", frame.head);
      set(el.highlight, "cx", frame.highlight.cx);
      frame.eyes.forEach((eye, i) => {
        set(el.eyes[i], "transform", eye.transform);
        set(el.eyeShapes[i], "d", eye.d);
        set(el.shines[i], "opacity", eye.shine);
        set(el.brows[i], "transform", frame.brows[i]);
      });
      set(el.mouth, "transform", frame.mouth.transform);
      set(el.mouthShape, "d", frame.mouth.d);
      set(el.mouthClip, "d", frame.mouth.d);
      set(el.tongue, "cy", frame.tongue.cy);
      set(el.tongue, "rx", frame.tongue.rx);
      set(el.tongue, "ry", frame.tongue.ry);
      set(el.tongue, "opacity", frame.tongue.opacity);
      set(el.cheeks, "opacity", frame.cheeks.opacity);
      set(el.cheekL, "transform", frame.cheeks.left);
      set(el.cheekR, "transform", frame.cheeks.right);
      frame.sparkles.forEach((s, i) => { set(el.sparkles[i], "transform", s.transform); set(el.sparkles[i], "opacity", s.opacity); });
      frame.zzz.forEach((z, i) => { set(el.zzz[i], "transform", z.transform); set(el.zzz[i], "opacity", z.opacity); });
      if (el.hat) {
        set(el.hat, "transform", frame.hat.transform);
        set(el.hatSkull, "d", frame.hat.skull);
        set(el.hatEdge, "d", frame.hat.edge);
        set(el.hatBoard, "d", frame.hat.board);
        set(el.hatCord, "d", frame.hat.cord);
        const shape = tasselShape(frame.hat.anchor, tassel.end, tassel.bend);
        set(el.tassel, "d", shape.d);
        set(el.tuft, "transform", shape.tuft);
      }
      node.dataset.expression = expression;
    };

    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const s = Object.fromEntries(Object.entries(POSES[controls.current.mood]).map(([key, value]) => [key, spring(value)])) as Record<keyof Pose, Spring>;
    const extra = { x: spring(), squash: spring(), pointerX: spring(), pointerY: spring(), hover: spring() };

    let raf = 0, last = 0, t = 0;
    let visible = true;
    let expression: string = controls.current.mood;
    let currentMood = controls.current.mood, moodStart = 0;
    let blinkAt = 1.5 + Math.random() * 2, blinkUntil = 0, doubleBlink = false;
    let glance = -1, previousGlance = -1, glanceUntil = 0, nextGlanceAt = 8 + Math.random() * 6;
    let nextSpecialAt = 70 + Math.random() * 40;
    let previousGestureId = controls.current.gesture?.id;
    let current: { type: RivaGesture; start: number } | null = null;
    let pointer = { x: 0, y: 0, at: -1e9 };
    let hovering = false, giggleUntil = 0;
    // Het hoedje heeft massa: het ligt op het hoofd, komt los als het hoofd sneller zakt dan het valt,
    // en glijdt scheef als het hoofd plots draait. Het kwastje is een slinger.
    const hat = { roll: spring(HAT_POSES[currentMood].roll), sink: spring(HAT_POSES[currentMood].sink), h: 0, v: 0, nextTossAt: Infinity };
    const previous = { liftVelocity: 0, angle: 0, x: 0 };
    let bob: Point | null = null, bobBefore: Point = [0, 0], lastAnchor: Point | null = null, spare = 0, bend = 0;
    const toss = (velocity: number) => {
      hat.v = Math.max(hat.v, velocity);
      hat.h = Math.max(hat.h, 0.01);
    };

    const play = (type: RivaGesture) => {
      if (media.matches) return;
      current = { type, start: t };
      nextSpecialAt = t + 90 + Math.random() * 60;
      wake();
    };
    tap.current = () => {
      const choices: RivaGesture[] = ["hop", "wave", "wiggle"];
      play(choices[Math.floor(Math.random() * choices.length)]);
      giggleUntil = t + 1.3;
    };

    const tick = (now: number) => {
      raf = 0;
      const reduced = media.matches || !controls.current.active;
      const dt = last && !reduced ? Math.min((now - last) / 1000, 0.05) : 1 / 60;
      last = now;
      if (!reduced) t += dt;
      const control = controls.current;

      if (control.gesture && control.gesture.id !== previousGestureId) {
        previousGestureId = control.gesture.id;
        play(control.gesture.type);
      }
      if (control.mood !== currentMood) {
        currentMood = control.mood;
        moodStart = t;
        glance = -1;
        nextGlanceAt = t + 10 + Math.random() * 6;
        // Each mood has its own entrance instead of one generic bounce.
        const kick: Partial<Record<RivaMood, () => void>> = {
          surprised: () => { s.lift.velocity -= 70; extra.squash.velocity -= 1.2; },
          idea: () => { s.lift.velocity -= 60; },
          celebrate: () => { s.lift.velocity -= 50; },
          happy: () => { extra.squash.velocity += 0.9; },
          sad: () => { s.lift.velocity += 25; },
          proud: () => { s.lift.velocity -= 30; },
        };
        if (!reduced) kick[control.mood]?.();
        if (!reduced && control.hat) {
          const up = HAT_KICK[control.mood];
          if (up) toss(up);
          if (control.mood === "sad") hat.roll.velocity -= 60;
        }
        hat.nextTossAt = control.mood === "celebrate" ? t + 3.2 : Infinity;
      }

      // Quiet expressions and the occasional flourish while nothing happens.
      const idle = control.mood === "idle" && !reduced;
      if (idle) {
        if (glance >= 0 && t >= glanceUntil) { glance = -1; nextGlanceAt = t + 10 + Math.random() * 6; }
        else if (glance < 0 && t >= nextGlanceAt) {
          const options = IDLE_GLANCES.map((_, i) => i).filter((i) => i !== previousGlance);
          glance = previousGlance = options[Math.floor(Math.random() * options.length)];
          glanceUntil = t + 2 + Math.random() * 0.8;
        }
        if (!current && t >= nextSpecialAt && t - pointer.at > 4) {
          const specials: RivaGesture[] = ["wave", "hop", "wiggle"];
          play(specials[Math.floor(Math.random() * specials.length)]);
        }
      } else glance = -1;

      const target: Pose = { ...POSES[control.mood], ...(glance >= 0 ? IDLE_GLANCES[glance].pose : {}) };
      expression = glance >= 0 ? IDLE_GLANCES[glance].name : control.mood;
      const age = t - moodStart;
      const beat = reduced ? null : moodBeat(control.mood, age);

      // Attention: a soft spring filters mouse flicks; after a while without
      // movement Riva looks ahead again.
      const covered = target.hold > 0.5 || control.mood === "sleeping";
      const tracking = control.followPointer && !covered && !reduced;
      let px = 0, py = 0;
      if (tracking && t - pointer.at < 6) {
        const rect = node.getBoundingClientRect();
        const dx = pointer.x - (rect.left + rect.width / 2), dy = pointer.y - (rect.top + rect.height * 0.48);
        const k = 150 + rect.width * 0.8;
        px = dx / Math.sqrt(dx * dx + k * k);
        py = dy / Math.sqrt(dy * dy + k * k);
      }
      const ax = reduced ? 0 : advance(extra.pointerX, px, dt, 60, 13);
      const ay = reduced ? 0 : advance(extra.pointerY, py, dt, 60, 13);
      const delight = reduced ? 0 : advance(extra.hover, (hovering && !covered ? 1 : 0) + (t < giggleUntil ? 1 : 0), dt, 140, 18);
      target.lookX = clamp(target.lookX + ax * 1.05, 1);
      target.lookY = clamp(target.lookY + ay * 0.95, 1);
      target.turn = clamp(target.turn + ax * 0.35, 1);
      target.tilt += ax * 1.5;
      target.happy = Math.min(1, target.happy + delight * 0.45);
      target.smile = Math.min(1, target.smile + delight * 0.35);
      target.blush = Math.min(1, target.blush + delight * 0.4);
      target.lift -= delight * 1;

      let gestureSquash = 0, tip = 0;
      if (beat) {
        target.tilt += beat.tilt;
        target.lift += beat.lift;
        target.mouthOpen = Math.min(1, target.mouthOpen + beat.mouthOpen);
        target.openL *= beat.openScale;
        target.openR *= beat.openScale;
        const breathe = Math.sin(t * (target.sleep > 0.5 ? 1.1 : 1.9));
        // While hiding Riva keeps still and does not breathe visibly.
        target.lift += breathe * 0.6 * target.energy * (1 - target.hold);
        gestureSquash = breathe * 0.005 * target.energy * (1 - target.hold);
      }
      if (current) {
        const p = (t - current.start) / GESTURE_LENGTH[current.type];
        if (p >= 1) current = null;
        else {
          const decay = 1 - p;
          switch (current.type) {
            case "hop":
              if (p < 0.16) { target.lift += 4; gestureSquash += 0.07; }
              else { const air = Math.sin(((p - 0.16) / 0.6) * Math.PI); if (p < 0.76) { target.lift -= air * 16; gestureSquash -= air * 0.05; } }
              break;
            // Zwaaien is het hoedje lichten.
            case "wave": target.tilt += 5 * Math.sin(p * Math.PI); target.happy = Math.max(target.happy, 0.5); tip = Math.sin(Math.min(1, p * 1.6) * Math.PI); break;
            case "nod": target.lift += Math.sin(p * Math.PI * 4) * 3.5 * decay; break;
            case "wiggle": target.tilt += Math.sin(p * Math.PI * 6) * 9 * decay; break;
          }
        }
      }

      // Blinks: every few seconds, sometimes twice.
      let blink = 0;
      if (!reduced) {
        if (t >= blinkAt) { blinkUntil = blinkAt + 0.16; doubleBlink = Math.random() < 0.2; blinkAt = t + 2.4 + Math.random() * 3.6; }
        if (t < blinkUntil) blink = Math.sin(((blinkUntil - t) / 0.16) * Math.PI);
        else if (doubleBlink && t < blinkUntil + 0.34 && t > blinkUntil + 0.1) blink = Math.sin(((t - blinkUntil - 0.1) / 0.24) * Math.PI);
      }

      const values = {} as Pose;
      for (const key of Object.keys(target) as (keyof Pose)[]) {
        if (reduced) { s[key].value = target[key]; s[key].velocity = 0; values[key] = target[key]; continue; }
        const [k, d] = STIFFNESS[key] ?? [170, 20];
        values[key] = advance(s[key], target[key], dt, k, d);
      }
      const squash = reduced ? 0 : advance(extra.squash, gestureSquash, dt, 260, 12);
      const x = (reduced ? values.turn * 3 : advance(extra.x, values.turn * 2 + ax * 1.5, dt, 120, 18)) * (1 - values.hold);
      // Het hoedje: per stemming een eigen ligging, met traagheid ten opzichte van het hoofd.
      const lie = HAT_POSES[control.mood];
      let hatNow: HatState;
      if (reduced || !control.hat) {
        hatNow = { roll: lie.roll, sink: lie.sink, h: 0, yaw: HAT.yaw + values.turn * 20 };
        hat.h = hat.v = 0;
      } else {
        if (t >= hat.nextTossAt) { toss(300); hat.nextTossAt = t + 3.2; }
        // Versnelling van het hoofd: zakt het sneller dan de zwaartekracht, dan komt het hoedje los.
        const fall = clamp((s.lift.velocity - previous.liftVelocity) / dt, 6000);
        const angle = s.tilt.velocity + s.headTilt.velocity;
        hat.roll.velocity -= clamp(angle - previous.angle, 400) * 0.85 + clamp(extra.x.velocity - previous.x, 200) * 1.6;
        previous.liftVelocity = s.lift.velocity; previous.angle = angle; previous.x = extra.x.velocity;
        if (hat.h > 0 || fall > GRAVITY) {
          hat.v += (fall - GRAVITY) * dt;
          hat.h += hat.v * dt;
          if (hat.h <= 0) {
            const impact = -hat.v;
            hat.h = 0;
            // Landen: even indeuken, nawiebelen en bij een harde landing een klein stuitertje.
            hat.v = impact > 90 ? impact * 0.26 : 0;
            if (hat.v) hat.h = 0.01;
            hat.sink.velocity += impact * 0.12;
            hat.roll.velocity += (Math.random() < 0.5 ? -1 : 1) * impact * 0.12;
          }
        }
        const air = hat.h > 0.5;
        hatNow = {
          roll: advance(hat.roll, lie.roll + tip * 7, dt, air ? 20 : 75, air ? 2.5 : 7),
          sink: advance(hat.sink, lie.sink - tip * 7, dt, 120, 12),
          h: hat.h,
          yaw: HAT.yaw + values.turn * 20 + values.lookX * 4,
        };
      }
      const frame = drawFrame({ pose: values, x, squash, blink, t, hat: hatNow });
      // Het kwastje: een slinger aan de hoek van de plank, met vaste stappen zodat het niet schokt.
      const anchor = frame.hat.anchor;
      if (reduced || !bob || !control.hat) { bob = restingTassel(anchor); bobBefore = [...bob]; spare = 0; bend = 0; }
      else {
        const step = 1 / 240, from = lastAnchor ?? anchor;
        spare = Math.min(spare + dt, 0.1);
        const steps = Math.floor(spare / step);
        spare -= steps * step;
        for (let i = 1; i <= steps; i++) {
          const pivot: Point = [from[0] + ((anchor[0] - from[0]) * i) / steps, from[1] + ((anchor[1] - from[1]) * i) / steps];
          const vx = (bob[0] - bobBefore[0]) * TASSEL_DAMPING, vy = (bob[1] - bobBefore[1]) * TASSEL_DAMPING;
          bobBefore = bob;
          let x: number = bob[0] + vx - pivot[0], y: number = bob[1] + vy + TASSEL_GRAVITY * step * step - pivot[1];
          // Het koord rekt niet en zwaait niet over de plank heen.
          let angle = Math.atan2(x, y);
          const limit = (TASSEL_REACH * Math.PI) / 180;
          if (Math.abs(angle) > limit) angle = Math.sign(angle) * limit;
          x = Math.sin(angle) * HAT.cord; y = Math.cos(angle) * HAT.cord;
          bob = [pivot[0] + x, pivot[1] + y];
        }
        // Het koord buigt een beetje tegen de zwaairichting in.
        const swing = ((bob[0] - bobBefore[0]) * (bob[1] - anchor[1]) - (bob[1] - bobBefore[1]) * (bob[0] - anchor[0])) / HAT.cord;
        bend = clamp(bend + (clamp(swing * 6, 3.5) - bend) * Math.min(1, dt * 12), 3.5);
      }
      lastAnchor = anchor;
      paint(frame, { end: bob, bend });
      if (!reduced && visible && !document.hidden) raf = requestAnimationFrame(tick);
    };
    const wake = () => { if (!raf && visible && !document.hidden) { last = 0; raf = requestAnimationFrame(tick); } };
    const onPointer = (event: PointerEvent) => { pointer = { x: event.clientX, y: event.clientY, at: t }; };
    const onLeaveWindow = () => { pointer.at = -1e9; };
    const onEnter = () => { hovering = true; };
    const onLeave = () => { hovering = false; };
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; if (visible) wake(); });
    observer.observe(node);
    window.addEventListener("pointermove", onPointer, { passive: true });
    document.documentElement.addEventListener("pointerleave", onLeaveWindow);
    node.addEventListener("pointerenter", onEnter);
    node.addEventListener("pointerleave", onLeave);
    document.addEventListener("visibilitychange", wake);
    media.addEventListener("change", wake);
    // Prop changes (mood, pause) need a frame even when the loop sleeps.
    wakeRef.current = wake;
    wake();
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("pointermove", onPointer);
      document.documentElement.removeEventListener("pointerleave", onLeaveWindow);
      node.removeEventListener("pointerenter", onEnter);
      node.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("visibilitychange", wake);
      media.removeEventListener("change", wake);
    };
  }, [hasHat]);
  useEffect(() => { wakeRef.current(); }, [mood, active, gesture, followPointer]);

  const g = (name: string) => `${id}-${name}`;
  const frame = initial;
  const look = hat ? HAT_STYLES[hat] : null;
  const firstTassel = tasselShape(frame.hat.anchor, restingTassel(frame.hat.anchor));
  const label = `Riva, de Curriva-mascotte: ${RIVA_LABELS[mood].toLowerCase()}`;
  const keys = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); tap.current(); }
  };
  return (
    <span
      ref={root}
      className={cn("relative block shrink-0 select-none", !size && "w-full", interactive && "cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}
      style={size ? { width: size, height: size } : undefined}
      data-riva
      data-mood={mood}
      {...(interactive ? { role: "button", tabIndex: 0, "aria-label": `${label}. Tik om te spelen.`, onClick: () => tap.current(), onKeyDown: keys } : decorative ? { "aria-hidden": true } : { role: "img", "aria-label": label })}
    >
      <svg viewBox={look ? `${VIEW.x} ${VIEW.y} ${VIEW.size} ${VIEW.size}` : `${CENTER.x - FACE_RADIUS} ${CENTER.y - FACE_RADIUS} ${FACE_RADIUS * 2} ${FACE_RADIUS * 2}`} className="block h-auto w-full overflow-visible" aria-hidden="true" focusable="false">
        <defs>
          <radialGradient id={g("face")} data-part="highlight" cx={frame.highlight.cx} cy={frame.highlight.cy} r="0.85">
            <stop offset="0" stopColor="#ffffff" /><stop offset="0.62" stopColor="#f5f8fd" /><stop offset="1" stopColor="#dde7f4" />
          </radialGradient>
          <clipPath id={g("mouth")}><path data-part="mouth-clip" d={frame.mouth.d} /></clipPath>
          {look && <>
            <linearGradient id={g("hat-top")} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={look.top[0]} /><stop offset="1" stopColor={look.top[1]} /></linearGradient>
            <linearGradient id={g("hat-skull")} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={look.skull[0]} /><stop offset="1" stopColor={look.skull[1]} /></linearGradient>
            <linearGradient id={g("tassel")} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={look.tassel[0]} /><stop offset="0.55" stopColor={look.tassel[1]} /><stop offset="1" stopColor={look.tassel[2]} /></linearGradient>
          </>}
        </defs>
        {/* Without a hat the frame is the face itself, so the shadow stays undrawn. */}
        <ellipse data-part="shadow" cx={CENTER.x} cy={GROUND + 3.5} rx={frame.shadow.rx} ry="2.6" fill={INK} opacity={frame.shadow.opacity} display={shadow && look ? undefined : "none"} />
        <g data-part="body" transform={frame.body}>
          <g data-part="head" transform={frame.head}>
          <circle cx={CENTER.x} cy={CENTER.y} r={FACE_RADIUS} fill={`url(#${g("face")})`} stroke={INK} strokeOpacity="0.07" strokeWidth="1.5" />
          <g transform={`translate(${CENTER.x} ${CENTER.y}) scale(${FEATURE_SCALE.toFixed(3)}) translate(${-CENTER.x} ${-CENTER.y})`}>
          <g data-part="cheeks" opacity={frame.cheeks.opacity} fill="#ff4f8b">
            <ellipse data-part="cheek-l" rx="7.5" ry="4.6" transform={frame.cheeks.left} />
            <ellipse data-part="cheek-r" rx="7.5" ry="4.6" transform={frame.cheeks.right} />
          </g>
          {frame.eyes.map((eye, i) => {
            const side = i ? "r" : "l";
            return <g key={side} data-part={`eye-${side}`} transform={eye.transform}>
              <path data-part={`eye-shape-${side}`} d={eye.d} fill={INK} />
              <g data-part={`shine-${side}`} opacity={eye.shine} fill="#ffffff"><circle cx="-2.6" cy="-4.4" r="2.7" /><circle cx="2.8" cy="3.4" r="1.2" opacity="0.6" /></g>
            </g>;
          })}
          {frame.brows.map((transform, i) => <path key={i} data-part={`brow-${i ? "r" : "l"}`} d="M-5.5 0.6Q0 -2.4 5.5 0.6" transform={transform} fill="none" stroke={INK} strokeWidth="3.2" strokeLinecap="round" opacity="0.85" />)}
          <g data-part="mouth" transform={frame.mouth.transform}>
            <path data-part="mouth-shape" d={frame.mouth.d} fill={INK} stroke={INK} strokeWidth="2.6" strokeLinejoin="round" />
            <ellipse data-part="tongue" clipPath={`url(#${g("mouth")})`} cx="0" cy={frame.tongue.cy} rx={frame.tongue.rx} ry={frame.tongue.ry} fill="#ff6f92" opacity={frame.tongue.opacity} />
          </g>
          </g>
          {look && <g data-part="hat" transform={frame.hat.transform}>
            <path data-part="hat-skull" d={frame.hat.skull} fill={`url(#${g("hat-skull")})`} />
            <path data-part="hat-edge" d={frame.hat.edge} fill={look.edge} strokeLinejoin="round" stroke={look.edge} strokeWidth="1" />
            <path data-part="hat-board" d={frame.hat.board} fill={`url(#${g("hat-top")})`} strokeLinejoin="round" stroke={look.top[0]} strokeWidth="1" />
            <path data-part="hat-cord" d={frame.hat.cord} stroke={look.tassel[1]} strokeWidth="1.8" strokeLinecap="round" fill="none" />
            <ellipse cx="0" cy={-HAT.lift} rx="3" ry="1.9" fill={look.tassel[1]} />
          </g>}
          </g>
        </g>
        {/* Het kwastje hangt in wereldcoördinaten, zodat de zwaartekracht altijd naar beneden trekt. */}
        {look && <g>
          <path data-part="tassel" d={firstTassel.d} stroke={look.tassel[1]} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <g data-part="tuft" transform={firstTassel.tuft}>
            <path d={TUFT_PATH} fill={`url(#${g("tassel")})`} />
            <path d={TUFT_STRANDS} stroke={INK} strokeOpacity="0.18" strokeWidth="0.7" strokeLinecap="round" fill="none" />
            <circle r="2.1" fill={look.tassel[1]} />
          </g>
        </g>}
        {/* Sparkles and z's float around the face; a bare face leaves them out. */}
        <g display={look ? undefined : "none"}>
          {frame.sparkles.map((sparkle, i) => <path key={i} data-part={`sparkle-${i}`} d={SPARKLE_PATH} fill={SPARKLES[i].color} transform={sparkle.transform} opacity={sparkle.opacity} />)}
          {frame.zzz.map((z, i) => <text key={i} data-part={`z-${i}`} transform={z.transform} opacity={z.opacity} fill="#7c8fb0" fontSize={i ? 8 : 11} fontWeight="700" fontFamily="inherit">z</text>)}
        </g>
      </svg>
    </span>
  );
}

export default Riva;
